import { useApi } from '../api/client.js';
import { Card, Pill } from '../components/ui.js';
import { useAsync } from '../hooks/useAsync.js';
import type { RecentSendRow } from '../api/types.js';

function humanRateTone(rate: number): 'success' | 'warning' | 'danger' {
  if (rate >= 0.75) return 'success';
  if (rate >= 0.6) return 'warning';
  return 'danger';
}

function fmtNum(n: number): string {
  return n.toLocaleString();
}

function fmtPct(f: number): string {
  return `${Math.round(f * 100)}%`;
}

export function RecentSends() {
  const api = useApi();
  const { state } = useAsync(() => api.recentSends(), [api]);

  if (state.status === 'loading') {
    return (
      <Card title="Recent sends">
        <div className="h-24 animate-pulse" style={{ background: 'var(--bg-secondary)' }} />
      </Card>
    );
  }
  if (state.status === 'error') {
    return (
      <Card title="Recent sends">
        <div role="alert" style={{ color: 'var(--danger-text)' }}>
          {state.error.message}
        </div>
      </Card>
    );
  }

  const rows: RecentSendRow[] = state.data;
  return (
    <Card title="Recent sends">
      {rows.length === 0 ? (
        <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
          No sends in the last 14 days.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                <th scope="col" className="py-2 pr-4 font-normal">
                  Send
                </th>
                <th scope="col" className="py-2 pr-4 font-normal">
                  Raw clicks
                </th>
                <th scope="col" className="py-2 pr-4 font-normal">
                  Filtered
                </th>
                <th scope="col" className="py-2 pr-4 font-normal">
                  Honeypot
                </th>
                <th scope="col" className="py-2 font-normal">
                  Human rate
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const filtered = r.bot + r.honeypot;
                return (
                  <tr
                    key={`${r.sendId}-${r.opco}`}
                    className="border-t"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <td className="py-2 pr-4 text-[14px]">
                      <div className="font-medium">{r.sendId}</div>
                      <div className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                        {r.opco}
                      </div>
                    </td>
                    <td className="py-2 pr-4 tabular-nums">{fmtNum(r.totalClicks)}</td>
                    <td className="py-2 pr-4 tabular-nums">{fmtNum(filtered)}</td>
                    <td className="py-2 pr-4 tabular-nums">{fmtNum(r.honeypot)}</td>
                    <td className="py-2">
                      <Pill tone={humanRateTone(r.humanRate)}>{fmtPct(r.humanRate)}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
